import React, { useMemo } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Chip, Typography } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ChargingSessionStatus } from '../types'
import { chargingSessionStatusTranslationKeys } from './charging-session-status-translation-keys'

const useStyles = makeStyles()(
  ({
    palette: {
      chargingSessions: { statusChip },
    },
  }) => ({
    chip: {
      height: '26px',
    },
    unknown: {
      backgroundColor: statusChip.unknown.background,
      color: statusChip.unknown.text,
    },
    active: {
      backgroundColor: statusChip.active.background,
      color: statusChip.active.text,
    },
    invalid: {
      backgroundColor: statusChip.invalid.background,
      color: statusChip.invalid.text,
    },
    pending: {
      backgroundColor: statusChip.pending.background,
      color: statusChip.pending.text,
    },
    completed: {
      backgroundColor: statusChip.completed.background,
      color: statusChip.completed.text,
    },
  })
)

interface ChargingSessionStatusChipChipProps {
  status: ChargingSessionStatus
}

/**
 * Displays a colored chip corresponding to each possible status of a charging session, with chip content
 * being status name.
 *
 * ACTIVE, COMPLETED, PENDING, INVALID, RESERVATION
 */
export const ChargingSessionStatusChip: React.FC<ChargingSessionStatusChipChipProps> = ({ status }) => {
  const { classes, cx } = useStyles()
  const { t } = useCustomTranslation()

  const statusClassnames: Record<ChargingSessionStatus, string> = useMemo(
    () => ({
      CHARGING: classes.active,
      ERROR: classes.active,
      DISCHARGING: classes.active,
      STANDBY: classes.active,
      FULL: classes.active,
      EMERGENCY_EXIT: classes.active,
      UNKNOWN: classes.pending,
      WAIT_PV: classes.pending,
      WAIT_AUTHENTICATION: classes.pending,
      WAITING_AFTER_SUCCESSFUL_AUTHENTICATION: classes.pending,
      SETUP: classes.pending,
      WAIT: classes.pending,
      FINISHED: classes.completed,
      FINISHED_NO_ACTION: classes.completed,
      UNAUTHORIZED: classes.invalid,
      NULL: '',
    }),
    [classes]
  )

  const label = t(chargingSessionStatusTranslationKeys[status]) || status
  const className = statusClassnames[status] || classes.unknown

  return status === 'NULL' ? <Typography>{label}</Typography> : <Chip label={label} className={cx(classes.chip, className)} />
}
